import React, { useEffect, useState } from "react";
import { Button, Divider, Header, Segment, Form } from "semantic-ui-react";
import { ControlledCheckbox, ControlledInput } from "./ControlledForm";
import { useDispatch } from "react-redux";
import { useMedia } from "react-media";
import { useParams } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { useToasts } from "react-toast-notifications";
import { updateProfile, forgotPassword } from "../actions/identity";
import "./AccountInfo.scss";
import Tooltip from "./Tooltip";
import { ControlledEmail } from "./ControlledEmail";
import { ControlledPhoneNumber } from "./global_components/form_components/ControlledPhoneNumber";
import ControlledAutofilledAddress from "./global_components/form_components/ControlledAutofilledAddress";
import { buildUpdateProfilePayload } from "../features/contacts/utils/buildUpdateProfilePayload";

export const AccountInfo = ({ item, email, emptyCache, label, tooltip, defaultValue }: any) => {
  const [editing, setEditing] = useState(false);
  const { addToast } = useToasts();
  const { site } = useParams<{ site: string }>();
  const formContext = useForm<any>();

  const control = formContext.control;
  const errors = formContext.errors;
  const showHomeAddressFields = !!!formContext.watch("sameMailAndPhysicalAddress");
  const isSmallScreen = useMedia({ query: "(max-width: 691px)" });
  const isMediumScreen = useMedia({ query: "(max-width: 991px)" });
  const dispatch = useDispatch();
  const onSubmit = async (data: any) => {
    const payload = buildUpdateProfilePayload(data);
    await dispatch(updateProfile(payload));
    setEditing(false);
    emptyCache();
  };
  const sendEmail = async (data: { site: string; email: string }) => {
    await dispatch(forgotPassword(data));
    addToast(`Check your email to reset your password`, { appearance: "success", autoDismiss: true });
    setEditing(false);
  };

  // Set form values to default when editing is toggled
  useEffect(() => {
    formContext.reset(defaultValue);
  }, [editing]);

  const valueControl = (label: string, item: any) => {
    if (label == "name") {
      return (
        <Form>
          <Form.Field required error={errors?.firstName}>
            <ControlledInput
              required
              control={control}
              name={"firstName"}
              label={"First Name"}
              defaultValue={defaultValue?.firstName}
              autoComplete='given-name'
              errors={errors}
            />
          </Form.Field>
          <Form.Field required error={errors?.lastName}>
            <ControlledInput
              required
              control={control}
              name={"lastName"}
              label={"Last Name"}
              defaultValue={defaultValue?.lastName}
              autoComplete='family-name'
              errors={errors}
            />
          </Form.Field>
        </Form>
      );
    } else if (label == "addresses") {
      return (
        <Form>
          <ControlledAutofilledAddress
            name='mailAddress'
            control={control}
            autofillCountry={true}
            showCountryField={true}
            defaultValue={defaultValue?.mailAddress}
            customAddressLabel='Mailing Address'
            errors={errors}
          />
          <Divider style={{ width: "100%" }} />
          <ControlledCheckbox
            className='tw-mt-4'
            name='sameMailAndPhysicalAddress'
            control={control}
            defaultValue={defaultValue?.sameMailAndPhysicalAddress}
            toggle
            label={"Physical Address Is the Same as Mailing"}
          />
          {showHomeAddressFields && (
            <div className='tw-mt-4'>
              <ControlledAutofilledAddress
                name='homeAddress'
                control={control}
                autofillCountry={true}
                showCountryField={true}
                defaultValue={defaultValue?.homeAddress}
                customAddressLabel='Physical Address'
                errors={errors}
              />
            </div>
          )}
        </Form>
      );
    } else if (label == "password") {
      return (
        <Button
          primary
          style={{ width: 240 }}
          onClick={() => sendEmail({ site: site, email: email })}
          content='Send Reset Password Email'
        />
      );
    } else if (label == "phone") {
      return (
        <Form>
          <Form.Field error={errors?.phone}>
            <ControlledPhoneNumber
              name={label}
              errors={errors}
              label='Phone'
              control={control}
              defaultValue={item}
              autoComplete='tel'
            />
          </Form.Field>
        </Form>
      );
    } else {
      return (
        <Form>
          <Form.Field required error={errors?.email}>
            <ControlledEmail
              control={control}
              name={label}
              label={"Email"}
              readonly={false}
              defaultValue={item}
              fieldName={"Email"}
              errors={errors}
              required
            />
          </Form.Field>
        </Form>
      );
    }
  };

  // render the read-only value of a field
  const readonlyValue = (label: string, item: any, editing: boolean) => {
    if (editing) return;
    if (label == "addresses") {
      return (
        <>
          {/* Mailing address value */}
          <div style={{ display: "flex", flexDirection: "row" }}>
            <p className='secondary'>Mailing Address</p>
            {tooltip && (
              <Tooltip>
                <b>Mailing address is used for:</b>
                <ul>
                  <li>Sending letters & renewal notices</li>
                  <li>Important documents</li>
                </ul>
              </Tooltip>
            )}
          </div>
          <Header> {item?.mailAddress} </Header>

          {/* Physical address value */}
          <div style={{ display: "flex", flexDirection: "row" }}>
            <p className='secondary'>Physical Address</p>
            {tooltip && (
              <Tooltip>
                <b>Physical address is used for:</b>
                <ul>
                  <li>Service area notifications</li>
                  <li>Permits where proof of residency is required</li>
                  <li>Service requests</li>
                </ul>
              </Tooltip>
            )}
          </div>
          <Header>{item?.physicalAddress}</Header>
        </>
      );
    }
    return <Header> {item} </Header>;
  };

  return (
    <>
      {/* edit/cancel/save buttons */}
      <div style={{ position: "absolute", right: isSmallScreen ? 20 : isMediumScreen ? 80 : 160, zIndex: 1 }}>
        {!editing && <Button className='tertiary' onClick={() => setEditing(!editing)} content='Edit' />}
        {editing && <Button className='muted-tertiary' onClick={() => setEditing(!editing)} content='Cancel' />}
        {editing && label != "password" && (
          <Button
            style={{ maxHeight: 48 }}
            className='tertiary'
            onClick={formContext.handleSubmit((data) => onSubmit(data))}
            content='Save'
          />
        )}
        {editing && label == "password" && (
          <Button style={{ maxHeight: 48 }} className='tertiary' onClick={() => setEditing(!editing)} content='Done' />
        )}
      </div>

      {/* form fields */}
      <div className='viewable-item' style={{ minHeight: 40 }}>
        {/* label and tooltip */}
        {label !== "addresses" && (
          <div style={{ display: "flex", flexDirection: "row" }}>
            <p className='secondary'>{label}</p>
            {tooltip && <Tooltip>{tooltip}</Tooltip>}
          </div>
        )}

        {/* readonly value */}
        {readonlyValue(label, item, editing)}

        {/* editing fields */}
        {editing && (
          <Segment basic style={{ marginTop: 0, paddingRight: 24 }}>
            <FormProvider {...formContext}>{valueControl(label, item)}</FormProvider>
          </Segment>
        )}
      </div>
    </>
  );
};

export default AccountInfo;
